/**
 * Phase PWA — IndexedDB-backed inspection queue.
 *
 * ObjectStore: ``pwa_inspection_queue``
 * Row shape:
 *   {
 *     id: <uuid>,
 *     kind: "inspection",
 *     rental_id: <str>,
 *     step: "metadata" | "photos" | "done",
 *     metadata: { condition, notes, damages },
 *     photos: [Blob, ...],
 *     attempts: <int>,
 *     last_error: <str|null>,
 *     created_at: <iso>,
 *     updated_at: <iso>,
 *   }
 */

const DB_NAME = "rentflow_pwa";
const DB_VERSION = 1;
const STORE = "pwa_inspection_queue";

let _dbPromise = null;

function _open() {
    if (_dbPromise) return _dbPromise;
    _dbPromise = new Promise((resolve, reject) => {
        if (typeof indexedDB === "undefined") {
            reject(new Error("IndexedDB unavailable"));
            return;
        }
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE)) {
                const os = db.createObjectStore(STORE, { keyPath: "id" });
                os.createIndex("by_step", "step");
                os.createIndex("by_created_at", "created_at");
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
    return _dbPromise;
}

function _uuid() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    // Fallback for older browsers.
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

export async function enqueueInspection({ rental_id, metadata, photos }) {
    const db = await _open();
    const row = {
        id: _uuid(),
        kind: "inspection",
        rental_id,
        step: "metadata",
        metadata: metadata || {},
        photos: Array.isArray(photos) ? photos : [],
        attempts: 0,
        last_error: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
    };
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, "readwrite");
        tx.objectStore(STORE).put(row);
        tx.oncomplete = () => resolve(row);
        tx.onerror = () => reject(tx.error);
    });
}

export async function listQueue() {
    let db;
    try { db = await _open(); }
    catch { return []; }
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, "readonly");
        const req = tx.objectStore(STORE).getAll();
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => reject(req.error);
    });
}

export async function updateRow(row) {
    const db = await _open();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, "readwrite");
        tx.objectStore(STORE).put({ ...row, updated_at: new Date().toISOString() });
        tx.oncomplete = () => resolve(row);
        tx.onerror = () => reject(tx.error);
    });
}

export async function deleteRow(id) {
    const db = await _open();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, "readwrite");
        tx.objectStore(STORE).delete(id);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}

export async function countPending() {
    const rows = await listQueue();
    return rows.filter((r) => r.step !== "done" && !r.last_error).length;
}

export async function clearAll() {
    const db = await _open();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, "readwrite");
        tx.objectStore(STORE).clear();
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}
